<template>
  <div
    class="v-upload__dragger"
    :class="{ 'is-dragover': dragover, 'is-disabled': disabled }"
    @dragleave.prevent="dragover = false"
    @dragover.prevent="onDragover"
    @drop.prevent="($event) => onDrop({ files: Array.from($event.dataTransfer?.files ?? []) })"
  >
    <VIcon icon="Upload" :type="disabled ? 'placeholder' : 'primary'" />
    <div>
      <slot>{{ i18n.dragAndDrop }} {{ i18n.or }} <VButton :disabled :label="i18n.clickToUpload" type="link" /></slot>
    </div>
    <span v-if="optional" class="v-upload__dragger-optional">{{ i18n.optional }}</span>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { i18nUploadDragger as i18n } from '../../utils/i18n.ts'
import VIcon from '../Icon/VIcon.vue'
import VButton from '../VButton.vue'

interface Props {
  disabled?: boolean
  optional?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const emit = defineEmits<{
  drop: [files: File[]]
}>()

defineSlots<{
  default?(): any
}>()

const dragover = ref(false)

function onDragover() {
  if (!props.disabled) {
    dragover.value = true
  }
}

function onDrop({ files }: { files: File[] }) {
  if (props.disabled) {
    return
  }

  dragover.value = false

  if (files.length > 0) {
    emit('drop', Array.from(files))
  }
}

defineExpose({
  dragover,
  onDrop,
  onDragover,
})
</script>

<style>
.v-upload__dragger {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  border: 1px dashed var(--color-primary);
  border-radius: 10px;
  padding: 20px;
  line-height: 1;
  cursor: pointer;
  transition:
    opacity,
    background-color var(--transition);

  &.is-dragover {
    border-width: 2px;
    background-color: var(--color-blue-1);
  }

  &.is-disabled {
    border-color: var(--color-grey-20);
    color: var(--color-grey-20);
  }

  &:hover {
    opacity: 0.8;
  }

  .v-icon {
    margin-bottom: 5px;
  }
}

.v-upload.is-disabled .v-upload__dragger {
  cursor: default;
}
.v-upload.has-errors .v-upload__dragger {
  border-color: var(--color-red);
}

.v-upload__dragger-optional {
  color: var(--color-text-placeholder);
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
